import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Shapes, Ruler, ChefHat, ArrowRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ConfiguratorHighlights() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const features = [{
    icon: Shapes,
    title: "Natural Aesthetics",
    description: "Rich grains and organic textures of genuine cedar and redwood."
  }, {
    icon: Ruler,
    title: "Precision Sealing",
    description: "Advanced protection against coastal moisture and UV damage."
  }, {
    icon: ChefHat,
    title: "Sustainable Sourcing",
    description: "FSC-certified timber ensuring eco-friendly outdoor living spaces."
  }];
  return <section data-section-id="1623" id="why-choose-our-wood-decks" ref={ref} className="relative w-full py-[160px] bg-background text-foreground overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-0 w-[500px] h-[500px] bg-chart-1/10 rounded-full blur-[100px] -translate-x-1/2" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-secondary/5 rounded-full blur-[120px] translate-x-1/3 translate-y-1/3" />
      </div>

      <div className="container relative mx-auto px-4 md:px-6 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          {}
          <div className={`lg:col-span-7 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"}`}>
            <div className="relative group">
              {}
              <div className="absolute inset-0 bg-chart-1/30 rounded-sm transform rotate-1 scale-105 opacity-60 transition-transform duration-500 group-hover:rotate-0 group-hover:scale-100" />
              
              {}
              <div className="relative bg-card/40 backdrop-blur-md border border-border/50 shadow-2xl rounded-sm overflow-hidden p-2 md:p-4">
                <div className="relative rounded-sm overflow-hidden bg-muted/50 aspect-[4/3] md:aspect-[16/10]">
                  <img src="https://images.unsplash.com/photo-1601058268499-e52658b8bb88?q=80&w=2070&auto=format&fit=crop" alt="Finished Cedar wood deck in Santa Cruz by PCH Builders" className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" />
                  
                  {}
                  <div className="absolute top-4 right-4 flex gap-2">
                    <div className="w-8 h-8 rounded-sm bg-background/90 backdrop-blur-sm shadow-sm flex items-center justify-center">
                      <div className="w-4 h-4 rounded-sm bg-chart-1" />
                    </div>
                    <div className="w-8 h-8 rounded-sm bg-background/90 backdrop-blur-sm shadow-sm flex items-center justify-center">
                      <div className="w-4 h-4 rounded-sm bg-chart-3" />
                    </div>
                  </div>
                  
                  <div className="absolute bottom-4 left-4 right-4 bg-background/90 backdrop-blur-md rounded-sm p-3 shadow-lg border border-border/40">
                    <div className="flex justify-between items-center">
                      <div className="space-y-1">
                        <div className="h-2 w-24 bg-muted-foreground/30 rounded-full" />
                        <div className="h-2 w-16 bg-muted-foreground/20 rounded-full" />
                      </div>
                      <div className="h-8 w-20 bg-chart-1/20 rounded-sm" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className={`lg:col-span-5 space-y-10 transition-all duration-1000 delay-300 ease-out ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`}>
            <div className="space-y-6">
              <h2 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight tracking-tight">
                The Timeless Appeal of Wood
              </h2>
              <p className="font-default text-lg text-muted-foreground leading-relaxed">
                Since 1990, PCH Builders has mastered the art of natural timber construction. 
                Our decks offer an organic warmth that synthetic materials simply cannot replicate, 
                engineered with proprietary sealing to withstand the California coast.
              </p>
            </div>

            <ul className="space-y-6">
              {features.map((feature, i) => <li key={i} data-index={i} className="group flex items-start gap-4 p-4 rounded-sm hover:bg-chart-1/5 transition-colors duration-300">
                  <div className="mt-1 p-2 rounded-sm bg-background shadow-sm border border-border/50 group-hover:border-chart-1/30 transition-colors">
                    <feature.icon className="w-6 h-6 text-chart-1 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-default text-xl font-semibold text-foreground mb-1">
                      {feature.title}
                    </h3>
                    <p className="font-default text-sm text-muted-foreground">
                      {feature.description}
                    </p>
                  </div>
                </li>)}
            </ul>

            <div className="pt-4">
              <Button asChild variant="ghost" size="lg" className="group border border-input hover:bg-chart-1/10 hover:text-foreground px-8 py-6 text-base h-auto rounded-sm transition-all duration-300">
                <Link to="#">
                  See Wood Portfolio
                  <ArrowRight className="ml-2 w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}